import { deckWordLemma } from '../utils/deckWords.js';
import {
  Card,
  conjugateVerb,
  expandKnownForms,
  isRetired,
  upNextFields,
  verbSpecFromGenerated,
} from '@lingoping/core';
import { StorageManager } from '../storage/index.js';
import { ensureCardImage } from './cardImageService.js';
import { CloudDecksApi, CloudDeckWord, getCloudDecksApi } from './cloudDecks.js';
import { unretireCard } from './knownCards.js';
import {
  buildPendingCard,
  forgetPendingCards,
  recordCouldntAdd,
  rememberPendingCard,
  sourceDeckFor as defaultSourceDeckFor,
} from './pendingCardStore.js';
import { createVocabularyCards, isRetryableCode } from './wordCardService.js';

/** What the learner can say about a deck word. */
export type DeckDecision = 'learn' | 'known' | 'notnow';

export interface DecisionResult {
  /** The forms added to the known list ("I know it"). */
  forms?: string[];
  /** The card being created ("Learn it"). */
  job?: LearnJob;
}

export interface DecisionContext {
  userId: string;
  deckId: string;
  language?: string;
  /** For tests: the deck service and learn queue to use instead of the shared ones. */
  api?: CloudDecksApi;
  queue?: LearnQueue;
}

/**
 * Every form that counts as known for a deck word: the library row's forms where it has them (a
 * prepared noun brings its plural, a verb its conjugated forms), otherwise just the word itself.
 */
export function knownFormsFor(word: CloudDeckWord): string[] {
  const library = word.word;
  if (library?.forms?.length) return library.forms;
  const meta = library?.linguistic_meta;
  const pos = library?.part_of_speech ?? undefined;
  let conjugation = library?.conjugation;
  let separable: string | undefined;
  if (!conjugation && pos === 'verb' && meta?.verb) {
    const spec = verbSpecFromGenerated(deckWordLemma(word), meta.verb);
    conjugation = conjugateVerb(spec);
    separable = spec.separable;
  }
  return expandKnownForms({
    lemma: deckWordLemma(word),
    part_of_speech: pos,
    linguistic_meta: { plural: meta?.plural ?? null, conjugation },
    separable,
  });
}

// ---------------------------------------------------------------------------------------------
// Learn: cards are created in the background, so a swipe never waits for the network
// ---------------------------------------------------------------------------------------------

export interface LearnJob {
  id: string;
  word: CloudDeckWord;
  deckId: string;
  userId: string;
  language: string;
  /** When the learner chose the word; it keeps its place in "Up next" even if the card is made later. */
  queuedAt: string;
  /**
   * 'done': the word has a card - ready, or a pending one that is prepared later (offline, daily
   * limit, server error) - or already had one. 'failed': the word cannot be a card (it is not
   * German); the learner is told once (`takeCouldntAdd`).
   */
  status: 'queued' | 'creating' | 'done' | 'failed' | 'cancelled';
  /**
   * "I know it" on one meaning of a word with several: the card is made and put away at once
   * (known per meaning; known words are kept per spelling and would cover every meaning).
   */
  retire?: boolean;
  card?: Card;
  /** Why the word could not be added (status 'failed'). */
  error?: string;
}

export interface LearnQueueDeps {
  createCards?: typeof createVocabularyCards;
  ensureImage?: (card: Card) => Promise<unknown>;
  /** The deck id to record on the card: null while the deck itself is not uploaded yet. */
  sourceDeckFor?: (deckId: string) => Promise<string | null>;
}

const BATCH_SIZE = 10;

export class LearnQueue {
  private jobs: LearnJob[] = [];
  private listeners = new Set<() => void>();
  private draining: Promise<void> | null = null;
  private counter = 0;

  constructor(private readonly deps: LearnQueueDeps = {}) {}

  /** Words still being turned into cards. */
  active(): LearnJob[] {
    return this.jobs.filter((j) => j.status === 'queued' || j.status === 'creating');
  }

  isActive(wordId: string): boolean {
    return this.active().some((j) => j.word.id === wordId);
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Resolves when every queued word has been handled. */
  async idle(): Promise<void> {
    while (this.draining) await this.draining;
  }

  enqueue(
    word: CloudDeckWord,
    ctx: { userId: string; deckId: string; language?: string },
    options: { retire?: boolean } = {}
  ): LearnJob {
    const job: LearnJob = {
      retire: options.retire,
      id: `learn_${Date.now()}_${++this.counter}`,
      word,
      deckId: ctx.deckId,
      userId: ctx.userId,
      language: ctx.language ?? 'de',
      queuedAt: new Date().toISOString(),
      status: 'queued',
    };
    this.jobs.push(job);
    this.emit();
    if (!this.draining) {
      this.draining = this.drain().finally(() => {
        this.draining = null;
        this.emit();
      });
    }
    return job;
  }

  /** Undo: stops the job, or removes the card / waiting entry it already made. */
  async cancel(job: LearnJob): Promise<void> {
    const before = job.status;
    job.status = 'cancelled';
    this.jobs = this.jobs.filter((j) => j !== job);
    if (before === 'done' && job.card) {
      await StorageManager.deleteCard(job.card.id);
      await forgetPendingCards([job.card.id]);
    }
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }

  private async drain(): Promise<void> {
    const createCards = this.deps.createCards ?? createVocabularyCards;
    const ensureImage = this.deps.ensureImage ?? ensureCardImage;
    for (;;) {
      const batch = this.jobs.filter((j) => j.status === 'queued').slice(0, BATCH_SIZE);
      if (batch.length === 0) return;
      for (const job of batch) job.status = 'creating';
      this.emit();

      let results: Awaited<ReturnType<typeof createVocabularyCards>>;
      try {
        results = await createCards(
          batch.map((j) => ({
            word: deckWordLemma(j.word),
            context: j.word.context ?? undefined,
            hint: j.word.hint ?? undefined,
            senseKey: isOneMeaning(j.word) ? (j.word.sense_key ?? '') : undefined,
          })),
          batch[0].userId,
          batch[0].language
        );
      } catch (error) {
        const reason = (error as Error)?.message || 'Could not create the card.';
        results = batch.map((j) => ({ word: j.word.lemma, error: reason }));
      }

      for (const [i, job] of batch.entries()) {
        // Undo may have cancelled the job while the request was in flight.
        if (job.status === 'cancelled') continue;
        const result = results[i];
        // The label points at a deck row; until the deck itself is uploaded it must stay empty.
        const sourceDeck = () => (this.deps.sourceDeckFor ?? defaultSourceDeckFor)(job.deckId);
        if (result?.card) {
          const fields = upNextFields({ kind: 'deck', deckId: job.deckId }, new Date(job.queuedAt));
          fields.source_deck_id = await sourceDeck();
          const card = {
            ...result.card,
            ...fields,
            ...(job.retire ? { retired_at: new Date().toISOString() } : {}),
          };
          await StorageManager.addCard(card);
          void ensureImage(card);
          job.card = card;
          job.status = 'done';
        } else if (result?.duplicate) {
          // Already a card (maybe under its dictionary form): nothing to add.
          job.status = 'done';
        } else if (result?.code === 'not_a_german_word' || result?.code === 'invalid_word') {
          // Never going to be a card: tell the learner once instead of keeping it around.
          job.error = result.error;
          await recordCouldntAdd(job.word.lemma, result.error ?? 'That is not a German word.');
          job.status = 'failed';
        } else {
          // Offline, a server error or the daily limit: the word is chosen all the same. It gets
          // a pending card now (visible in Up next as "Preparing…") and its text later.
          const card = buildPendingCard({
            lemma: job.word.lemma,
            userId: job.userId,
            language: job.language,
            source: { kind: 'deck', deckId: job.deckId },
            queuedAt: job.queuedAt,
            ...(isOneMeaning(job.word)
              ? { senseKey: job.word.sense_key ?? '', senseHint: job.word.sense_hint ?? null }
              : {}),
          });
          if (job.retire) card.retired_at = new Date().toISOString();
          card.source_deck_id = await sourceDeck();
          await StorageManager.addCard(card);
          await rememberPendingCard(card.id, {
            context: job.word.context ?? undefined,
            hint: job.word.hint ?? undefined,
            code: isRetryableCode(result?.code) ? result?.code : 'other',
            lastAttemptAt: new Date().toISOString(),
            attempts: 1,
          });
          job.card = card;
          job.status = 'done';
        }
      }
      // Finished jobs are not kept; whoever started one still holds it (for undo).
      this.jobs = this.jobs.filter((j) => j.status === 'queued' || j.status === 'creating');
      this.emit();
    }
  }
}

/** The learn queue every screen shares. */
export const deckLearnQueue = new LearnQueue();

// ---------------------------------------------------------------------------------------------
// The three choices
// ---------------------------------------------------------------------------------------------

/**
 * Applies a choice on a deck word:
 * - known: the word and all its forms go to the known list;
 * - learn: a card is created in the background and joins "Up next to learn" in deck order;
 * - notnow: the word moves to the end of the deck for this learner.
 */
/**
 * Whether a deck entry is ONE meaning of a word with several ("Bank (Möbel)"): an import makes one
 * entry per meaning of such a word. Its decisions are about that meaning only.
 */
export function isOneMeaning(word: CloudDeckWord): boolean {
  return !!word.sense_key || !!word.sense_hint;
}

export async function applyDeckDecision(
  word: CloudDeckWord,
  decision: DeckDecision,
  ctx: DecisionContext
): Promise<DecisionResult> {
  if (decision === 'known' && isOneMeaning(word)) {
    // Known for this meaning only: its card is made and put away at once.
    return { job: (ctx.queue ?? deckLearnQueue).enqueue(word, ctx, { retire: true }) };
  }
  if (decision === 'known') {
    const forms = knownFormsFor(word);
    await StorageManager.addKnownForms(deckWordLemma(word), forms, ctx.language ?? 'de');
    return { forms };
  }
  if (decision === 'learn') {
    return { job: (ctx.queue ?? deckLearnQueue).enqueue(word, ctx) };
  }
  await (ctx.api ?? (await getCloudDecksApi())).deferWord(word.id);
  return {};
}

/** Reverts the choice just made (single-step undo). */
export async function undoDeckDecision(
  word: CloudDeckWord,
  decision: DeckDecision,
  result: DecisionResult,
  ctx: Pick<DecisionContext, 'api' | 'queue'> = {}
): Promise<void> {
  if (decision === 'known' && result.job) {
    await (ctx.queue ?? deckLearnQueue).cancel(result.job);
  } else if (decision === 'known') {
    await StorageManager.removeKnownGroup(deckWordLemma(word));
  } else if (decision === 'learn') {
    if (result.job) await (ctx.queue ?? deckLearnQueue).cancel(result.job);
  } else {
    await (ctx.api ?? (await getCloudDecksApi())).undeferWord(word.id);
  }
}

/**
 * "Learn it" on a deck word the learner had marked as known (deck page). The word leaves the known
 * words (its whole group, like removing it in the Known words tab) and is learned after all: a card
 * the learner retired for it is shown again with its history; otherwise a card is made like any
 * "Learn it" choice.
 */
export async function relearnKnownWord(
  word: CloudDeckWord,
  ctx: DecisionContext
): Promise<DecisionResult> {
  const key = (text?: string | null) => text?.trim().toLowerCase() ?? '';
  const names = new Set([key(word.word?.lemma), key(word.lemma)].filter(Boolean));

  if (isOneMeaning(word)) {
    // Known for this meaning only (its card was put away): that card comes back.
    const sense = word.sense_key ?? '';
    const mine = (await StorageManager.getCards()).filter(
      (card) => names.has(key(card.lemma || card.front_text)) && (card.sense_key ?? '') === sense
    );
    if (mine.some((card) => !isRetired(card))) return {};
    if (mine.length > 0) {
      for (const card of mine) await unretireCard(card);
      return {};
    }
    return { job: (ctx.queue ?? deckLearnQueue).enqueue(word, ctx) };
  }

  // The word may be known under another dictionary form than the deck writes ("Tische" -> Tisch).
  const groups = new Map<string, string>([[key(deckWordLemma(word)), deckWordLemma(word)]]);
  for (const known of await StorageManager.getKnownWords()) {
    if (!names.has(key(known.lemma))) continue;
    const base = known.base_lemma ?? known.lemma;
    groups.set(key(base), base);
  }
  for (const base of groups.values()) await StorageManager.removeKnownGroup(base);

  const cards = (await StorageManager.getCards()).filter((card) =>
    names.has(key(card.lemma || card.front_text))
  );
  // Already a live card: taking it off the known words was all that was needed.
  if (cards.some((card) => !isRetired(card))) return {};
  if (cards.length > 0) {
    for (const card of cards) await unretireCard(card);
    return {};
  }
  return { job: (ctx.queue ?? deckLearnQueue).enqueue(word, ctx) };
}
